import formatDate from "@feniks-pwa/web-app/web/utils/data/parser/dateAndTime/formatDate";
import {
  arrayOf,
  bool,
  func,
  instanceOf,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";
import { useCallback, useMemo } from "react";

import Price from "web/Layout/Common/Price";
import __ from "web/Layout/Translations";

import ToolTip from "web/Components/Common/ToolTip";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import getBenefitLegends from "web/utils/page/product/subscription/getBenefitLegends";
import { getIdFromIri } from "web/utils/page/product/subscription/iri";
import { getPointsBankName } from "web/utils/page/product/subscription/prepareBenefitsPricesOption";

import {
  BENEFIT_EMPLOYER_PRICE_TYPE,
  IRI,
  SOURCE_FOUND_LABEL_SUMMARY,
  pointsPerMonth,
  zlotyPerMonth,
} from "web/constants/benefits";

import { SubscriptionMethod } from "web/types/Subscription";

import { useGetBanksQuery } from "web/features/pointsBank/pointsBankApiSlice";

import defaultClasses from "../../ConfigurationStep/Benefits/Item/item.scss";
import ReadMore from "../../ConfigurationStep/Benefits/ReadMore";
import Definition from "../../ConfigurationStep/Legend/Definition";
import checkIfReadMoreShouldBeDisplayed from "../../ConfigurationStep/utils/checkIfReadMoreShouldBeDisplayed";
import classes from "./benefitHeader.scss";

const definitionClass = {
  root: defaultClasses.definition,
  tooltip: defaultClasses.detailsTooltip,
};

const CheckoutSubscriptionBenefitHeader = ({
  extendedView,
  name,
  description,
  quantity,
  employerPriceType,
  employerPrice,
  employerPriceAlternativeText,
  employeePrice,
  attachments,
  setModal,
  relationBenefitTypeConfiguration,
  relationBenefitFreeTypeConfiguration,
  benefitDraftUUID,
  benefitEmployeeGroupPrice,
  benefitGroupType,
  endsAt,
  expectedEndsAt,
  groupName,
  method,
}) => {
  const { data: dataBanks } = useGetBanksQuery();
  const { items: pointsBanks } = dataBanks || {};

  const uuid = getIdFromIri(benefitDraftUUID, IRI.BENEFIT);
  const canIShowEmployerPrice =
    employerPriceType !== BENEFIT_EMPLOYER_PRICE_TYPE.HIDDEN;
  const isCoFinanced = method === SubscriptionMethod.CO_FINANCED;
  const [benefitLegendsData, benefitLegendIcons] = useMemo(() => {
    // get shown benefit TYPE icons
    const benefitLegends = getBenefitLegends(
      relationBenefitTypeConfiguration,
      relationBenefitFreeTypeConfiguration
    );

    const benefitIcons = benefitLegends.map(
      ({ name: iconName, icon: Icon, message }) => {
        return (
          <Definition
            key={iconName}
            message={message}
            classes={definitionClass}
          >
            <Icon className={classes.detailsIcon} />
          </Definition>
        );
      }
    );
    return [benefitLegends, benefitIcons];
  }, []);

  const handleToggleModal = useCallback(() => {
    setModal(
      <ReadMore
        title={name}
        description={description}
        attachments={attachments}
        benefitLegends={benefitLegendsData}
        benefitDraftUUID={uuid}
        groupName={groupName}
      />
    );
  }, []);

  const hasReadMore = checkIfReadMoreShouldBeDisplayed({
    visibleDescription: description,
    attachments,
  });

  // UMS data
  const umsData = benefitEmployeeGroupPrice?.currentPriceVariant
    ? Object(benefitEmployeeGroupPrice?.currentPriceVariant)
    : Object(benefitEmployeeGroupPrice);
  const {
    employeePayUPrice: employeePayU,
    employeePrice: employee,
    employerPrice: employer,
    pointsBankPrice: points,
  } = umsData;
  const pointsBankLabel = getPointsBankName(
    pointsBanks,
    benefitEmployeeGroupPrice?.pointsBankId
  );

  const umsPricesData = [
    {
      value: "employeePayU",
      price: employeePayU,
      label: SOURCE_FOUND_LABEL_SUMMARY.employeePayU,
    },
    {
      value: "employee",
      price: employee,
      label: SOURCE_FOUND_LABEL_SUMMARY.employee,
    },
    {
      value: "employer",
      price: employer,
      label: SOURCE_FOUND_LABEL_SUMMARY.employer,
    },
    {
      value: "points",
      price: points,
      label: pointsBankLabel,
    },
  ];

  const createLabelWithPrefix = (value, label) => {
    if (!canIShowEmployerPrice && !isCoFinanced)
      return employerPriceAlternativeText;

    const coFinancedprefix = SOURCE_FOUND_LABEL_SUMMARY[value];

    if (value === "points") {
      return isCoFinanced
        ? `${__(coFinancedprefix)} ${label}`
        : `100% ${__(coFinancedprefix)} ${label}`;
    }

    return isCoFinanced ? __(coFinancedprefix) : `100% ${__(coFinancedprefix)}`;
  };

  const handleCoFinancedOption = (umsPricesData) => {
    const coFinanced = umsPricesData.filter(
      (priceData) => priceData.price && priceData.price !== "0.00"
    );

    if (isCoFinanced) {
      const coFinancedFinalCost =
        coFinanced.length > 1
          ? coFinanced.reduce(
              (prev, curr) =>
                parseFloat(prev.price, 10) + parseFloat(curr.price, 10)
            )
          : coFinanced[0]?.price;

      return (
        <>
          <div className={`${classes.item} ${classes.item__ums}`}>
            <span
              className={classes.sourceFound__title}
              data-t1="fundingSource"
            >
              {__("Współfinansowany")}
            </span>
            {coFinanced.map((financedEle) => (
              <div className={classes.item__sourceFound}>
                {financedEle.price}
                {__(
                  financedEle.value === "points"
                    ? pointsPerMonth
                    : zlotyPerMonth
                )}{" "}
                - {createLabelWithPrefix(financedEle.value, financedEle.label)}
              </div>
            ))}
            {!canIShowEmployerPrice && employerPriceAlternativeText ? (
              <div
                className={classes.item__sourceFound}
                data-t1="hiddenCostMessage"
              >
                {employerPriceAlternativeText}
              </div>
            ) : null}
          </div>
          <div className={`${classes.item} ${classes.item__ums}`}>
            <Price value={coFinancedFinalCost} currency={__(zlotyPerMonth)} />
          </div>
        </>
      );
    }

    return null;
  };

  const coFinancedJSX = handleCoFinancedOption(umsPricesData);
  const currentUmsPriceData = umsPricesData.filter(
    (ele) => ele.price && ele.price !== "0.00"
  );
  const umsCurrency =
    currentUmsPriceData[0]?.value === "points" ? pointsPerMonth : zlotyPerMonth;

  const resultEndDate = expectedEndsAt || endsAt;

  return (
    <div className={classes.root}>
      <div className={classes.name}>
        <span>{name}</span>
        {hasReadMore || isArrayHasItems(benefitLegendIcons) ? (
          <div className={classes.other}>
            {hasReadMore ? (
              <button
                onClick={handleToggleModal}
                className={classes.more}
                type="button"
              >
                {__("czytaj więcej")}
              </button>
            ) : null}
            {benefitLegendIcons}
          </div>
        ) : null}
      </div>
      <div className={classes.details}>
        <div
          className={classes.item}
          data-t1="numberOfPeople"
          data-t2={quantity}
        >{`${quantity} ${__("os.")}`}</div>
        {extendedView ? (
          <>
            {benefitGroupType === "2" ? (
              <>
                {coFinancedJSX || (
                  <>
                    <div className={classes.item}>
                      {createLabelWithPrefix(
                        currentUmsPriceData[0]?.value,
                        currentUmsPriceData[0]?.label
                      )}
                    </div>
                    <div className={classes.item}>
                      <Price
                        value={currentUmsPriceData[0]?.price}
                        currency={__(umsCurrency)}
                      />
                    </div>
                  </>
                )}
                <div className={classes.item}>
                  {resultEndDate
                    ? formatDate(resultEndDate)
                    : __("Czas nieokreślony")}
                  {expectedEndsAt && (
                    <ToolTip
                      customStyles={{
                        root: defaultClasses.tooltipRoot,
                      }}
                      variant="right"
                      message={__(
                        "Data wskazuje do kiedy opłacone jest świadczenie"
                      )}
                    />
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={classes.item}>
                  <Price
                    value={canIShowEmployerPrice ? employerPrice : null}
                    currency={__(zlotyPerMonth)}
                    alternativePlaceholder={employerPriceAlternativeText}
                    dataT1="employerCost"
                  />
                </div>
                <div className={classes.item}>
                  <Price
                    value={employeePrice}
                    currency={__(zlotyPerMonth)}
                    dataT1="employeeCost"
                  />
                </div>
              </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

CheckoutSubscriptionBenefitHeader.propTypes = {
  extendedView: bool.isRequired,
  name: string.isRequired,
  description: string,
  quantity: number.isRequired,
  employerPriceType: string,
  employerPrice: string,
  employeePrice: string,
  employerPriceAlternativeText: string,
  attachments: arrayOf(shape({})),
  setModal: func,
  relationBenefitTypeConfiguration: string,
  relationBenefitFreeTypeConfiguration: string,
  benefitDraftUUID: string.isRequired,
  benefitGroupType: string.isRequired,
  benefitEmployeeGroupPrice: shape({}),
  endsAt: oneOfType([string, instanceOf(null)]),
  groupName: string.isRequired,
};

CheckoutSubscriptionBenefitHeader.defaultProps = {
  employerPrice: "",
  employeePrice: "",
  employerPriceAlternativeText: "",
  description: "",
  attachments: [],
  relationBenefitFreeTypeConfiguration: "",
  employerPriceType: "",
  relationBenefitTypeConfiguration: null,
  setModal: () => {},
  benefitEmployeeGroupPrice: {},
  endsAt: null,
};

export default CheckoutSubscriptionBenefitHeader;
